// ─── Beetsol ContentHealth (v2) ──────────────────────────────────────────────
// New file: src/components/Insights/ContentHealth.jsx
//
// Route: /insights/content-health
// Sidebar: Insights → Content Health
//
// What it shows (N5): 'Left without an answer', one row per item people said No to (people per audience, Type, Exit
// point, Average rating, Last edited, and under its name the questions that ended in a No), or a sentence when no one
// was. The audience, period and cohort come from the dashboard's address (the audience pills stay until N5 point 1
// removes them); ?section=attention scrolls to 'Left without an answer'.
// ─────────────────────────────────────────────────────────────────────────────

// ═══════════════════════════════════════════════════════════════════════════
// ⚠️ DATA WIRING IS PROVISIONAL — see docs/INSIGHTS_PROVISIONAL.md
// Design final. Data-fetch (search "SEAM-5") wired to insightsService from
// scratch. If real wired Insights files exist, prefer theirs + apply this design.
// ═══════════════════════════════════════════════════════════════════════════
import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Box, Button, Typography } from '@mui/material';
import { tokens } from '../../theme';
import { insightsService } from '../../services/api';
import { useAuthorContentNavigation } from '../../hooks/useAuthorContentNavigation';
import { useInsightsAdminRole } from '../../hooks/useInsightsAdminRole';
import TypeTag from '../shared/TypeTag';
import EmptyState from '../common/EmptyState';
import InsightsFilterLine from './InsightsFilterLine';
import InsightsPager from './InsightsPager';

const T = tokens;

// Removed 2 Nov 2026 (N5 pts 1, 15): the page's own audience pills (FilterPill). The audience comes from the dashboard
// with the period and cohort, shown in one read-only line (InsightsFilterLine); the page has no audience control.

const Th = ({ children, right }) => (
  <th style={{ fontSize: '11px', color: T.color.textSecondary, letterSpacing: '0.04em', textTransform: 'uppercase', padding: '9px 18px', textAlign: right ? 'right' : 'left', fontWeight: 400, borderBottom: `0.5px solid ${T.color.borderTertiary}`, fontFamily: T.font.family, background: T.color.bgPrimary }}>
    {children}
  </th>
);

// Exit point: the average time before an unresolved exit, as the card shows it ('45s', '1:49'), or a dash when none
// was recorded (K6.1 pt 9d, N5 pt 16). Removed 11 Oct 2026 (K6.1 pt 9d): a display chosen by a content type that was
// guessed from the drop-off bucket, so every row took the video display: the most common 10-second bucket of visit
// times, or a dash. Removed 12 Oct 2026 (N5 pt 16): 'Not recorded' in place of the dash.
const ExitPoint = ({ exitPoint }) => (
  <Typography sx={{ fontSize: T.font.size.sm, color: T.color.textSecondary, fontFamily: T.font.family }}>{exitPoint || '-'}</Typography>
);

// The Type tag (N5 pt 15) is the shared TypeTag (N10), from the item's address as the server reads it
// (utils/contentType.js).

// The question line under a content name stays on one line, cut at the end of a word with '...' when too long (N5 pt
// 15). Removed 23 Oct 2026 (N5 pt 15): letting it wrap onto more lines.
// The whole line, count and question together, fits the 420px it may take at its 11px type (about 64 characters): a
// question that fits in what is left after "N questions · Latest: " shows whole, and a longer one is cut to 3 characters
// less, so the "..." the cut adds fits too. The browser's own "…" stays as the last resort for unusually wide letters or
// a narrow screen. Removed 23 Oct 2026 (N5 pt 15, PR #8 review): 64 characters for the question alone, so with the count
// in front the browser cut it mid-word with "…"; then the "..." left out of the count, so a cut line ran 3 characters
// over; then the 3 characters set aside before deciding to cut, so a question that fit whole was cut.
const QUESTION_LINE_MAX = 64;
const questionLine = (row) => {
  const count = `${row.question_count} ${row.question_count === 1 ? 'question' : 'questions'}`;
  if (!row.latest_question) return count;
  const prefix = `${count} · Latest: `;
  const room = QUESTION_LINE_MAX - prefix.length;
  const question = String(row.latest_question);
  return `${prefix}${question.length <= room ? question : cutAtWord(question, Math.max(16, room - 3))}`;
};
const cutAtWord = (text, max = QUESTION_LINE_MAX) => {
  const t = String(text || '');
  if (t.length <= max) return t;
  const cut = t.slice(0, max + 1);
  const end = cut.lastIndexOf(' ');
  return `${(end > 0 ? cut.slice(0, end) : t.slice(0, max)).replace(/[\s,.;:]+$/, '')}...`;
};

// A date as '12 Oct 2026' (N5 pt 15, Last edited), or a dash.
const lastEditedText = (iso) => (iso ? new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '-');

// A header that re-sorts its table (N5 pts 6, 19): the first click sorts by it, highest first; the next flips the order.
const SortTh = ({ children, right, active, order, onClick, colSpan, rowSpan }) => (
  <th colSpan={colSpan} rowSpan={rowSpan} onClick={onClick} style={{ fontSize: '11px', color: active ? T.color.textPrimary : T.color.textSecondary, letterSpacing: '0.04em', textTransform: 'uppercase', padding: '9px 18px', textAlign: right ? 'right' : 'left', fontWeight: 400, borderBottom: `0.5px solid ${T.color.borderTertiary}`, fontFamily: T.font.family, background: T.color.bgPrimary, cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}>
    {children}{active ? (order === 'asc' ? ' ↑' : ' ↓') : ''}
  </th>
);

const Cell = ({ children, right, muted }) => (
  <td style={{ fontSize: '13px', color: muted ? T.color.textSecondary : T.color.textPrimary, padding: '11px 18px', textAlign: right ? 'right' : 'left', fontFamily: T.font.family, borderBottom: `0.5px solid ${T.color.borderTertiary}` }}>
    {children}
  </td>
);

// The shared EmptyState with a sentence on its own in the sections' frame, spaced as the table's cells (N5 pts 8, 21).
// tone="neutral" for the all-clear sentence, which stays grey (K2.3 pt 14.1).
const Empty = ({ message, tone }) => (
  <Box sx={{ background: T.color.bgPrimary, border: `0.5px solid ${T.color.borderTertiary}`, borderRadius: T.radius.lg, overflow: 'hidden', padding: '14px 18px' }}>
    <EmptyState framed={false} title={null} message={message} tone={tone} />
  </Box>
);

// Removed 2 Nov 2026 (N5 pt 4): this page's own copy of the pager; the Insights pages share one (InsightsPager).

// Removed 14 Oct 2026 (N5 pt 18): the Archive confirmation dialog, with the Low engagement section it served (below).

// Removed 12 Oct 2026 (N5 pt 7): the CSV export helper and its Export CSV button. The page is view-only in v1;
// downloads return on the Data Infra tab (DI2, DI3).

// ── Main component ────────────────────────────────────────────────────────────
const ContentHealth = () => {
  const navigateToContent = useAuthorContentNavigation();
  const roleChecked = useInsightsAdminRole();
  const [searchParams] = useSearchParams();
  const attentionRef = useRef(null);

  // The dashboard's audience, period and cohort, read-only here (N5 pts 1, 15): the page has no filter control.
  const audience = searchParams.get('audience') || 'all';
  const period = searchParams.get('period') || '7d';
  const cohort = searchParams.get('cohort') || 'all';
  // Left without an answer: its own page and order, from the server (N5 pts 5, 6, 19). Removed 12 Oct 2026
  // (N5 pt 19): the Unresolved rate / Exit point / Views pills, whose choice the server never read.
  const [left,     setLeft]     = useState(null); // { items, total }
  const [leftPage, setLeftPage] = useState(1);
  // It opens sorted by the audience shown, External when both show (N5 pt 15.1). Removed 2 Nov 2026 (N5 pt 15.1):
  // opening sorted by Left without an answer, both audiences added up.
  const [leftSort, setLeftSort] = useState({ by: audience === 'internal' ? 'internal' : 'external', order: 'desc' });
  // Rows whose latest 2 questions are shown (N5 pt 15).
  const [openQuestions, setOpenQuestions] = useState(() => new Set());
  const toggleQuestions = (id) => setOpenQuestions((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const PER_PAGE = 5;
  const section = searchParams.get('section');

  const handleEditContent = async (event, contentId) => {
    event.stopPropagation();
    try {
      await navigateToContent(contentId);
    } catch (err) {
      console.error('Unable to open content form:', err);
    }
  };

  // Removed 2 Nov 2026 (N5 pts 1, 15): writing the page's audience choice back to the address, with the link's section
  // (K3 pt 3); the page has no audience control, so the address keeps the dashboard's filters and the section as sent.

  // Auto-scroll to attention section if ?section=attention
  useEffect(() => {
    if (section === 'attention' && attentionRef.current && left) {
      setTimeout(() => {
        attentionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 300);
    }
  }, [section, left]);

  // Removed 14 Oct 2026 (N5 pt 18): the Low engagement data fetch (SEAM-5), with its section.

  // A new filter or order starts Left without an answer again from its first page.
  useEffect(() => { setLeftPage(1); }, [audience, period, cohort, leftSort]);

  // Left without an answer, one page at a time in the chosen order (N5 pts 5, 15, 19), for the period as dateRange 7,
  // 30 or 90 and the cohort, which the server reads (N5 pts 1, 14; K3 pt 2). Removed 12 Oct 2026 (N5 pt 5): showing
  // only the first page the server sent, with no further pages. Removed 11 Oct 2026 (N5 pt 14): sending 'period',
  // which the server ignores, so the page always showed 7 days.
  useEffect(() => {
    if (!roleChecked) return;
    let cancelled = false;
    insightsService.getFrictionPoints({
      audience, cohort, dateRange: parseInt(period, 10) || 7,
      sort: leftSort.by, order: leftSort.order, page: leftPage, limit: PER_PAGE,
    })
      .then((r) => {
        if (cancelled) return;
        const ok = r && r.data && r.data.success;
        setLeft({ items: ok ? (r.data.data || []) : [], total: ok ? Number(r.data.pagination?.total) || 0 : 0 });
      })
      .catch((err) => { if (!cancelled) console.error(err); });
    return () => { cancelled = true; };
  }, [audience, period, cohort, leftSort, leftPage, roleChecked]);

  const sortLeft = (by) => setLeftSort((cur) => (cur.by === by ? { by, order: cur.order === 'desc' ? 'asc' : 'desc' } : { by, order: 'desc' }));
  // The audience columns follow the inherited audience: All shows both, External or Internal only that one (N5 pt 15).
  const showExt = audience !== 'internal';
  const showInt = audience !== 'external';
  // No one left without an answer for these filters: the page's sentence instead of the table (N5 pts 8, 21).
  const nobodyLeft = !!left && left.total === 0;

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ marginBottom: '24px' }}>
        <Typography sx={{ fontSize: T.font.size.xl, fontWeight: T.font.weight.medium, color: T.color.textPrimary, letterSpacing: '-0.02em', fontFamily: T.font.family }}>
          Content Health
        </Typography>
        <InsightsFilterLine audience={audience} period={period} cohort={cohort} />
      </Box>

      {/* Left without an answer (N5 pts 15-17, 19): people, not rates, per audience; no red rows, no coloured values; rows
          are not clickable (N5 pt 3). Removed 12 Oct 2026 (N5 pts 3, 15): the title 'Content needing attention' and
          its subtitle 'High unresolved rate', the Ext / Int unresolved percentages, the red highlight above 60%, the
          row click that opened an insight the server never sent, and the line 'Click a row to view the insight'. */}
      {nobodyLeft ? (
        <Empty tone="neutral" message="No one was left without an answer in this period." />
      ) : (
      <Box ref={attentionRef} sx={{ background: T.color.bgPrimary, border: `0.5px solid ${T.color.borderTertiary}`, borderRadius: T.radius.lg, marginBottom: '10px', overflow: 'hidden', width: '100%' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 18px', borderBottom: `0.5px solid ${T.color.borderTertiary}`, flexWrap: 'wrap', gap: '6px' }}>
          <Box>
            <Box component="span" sx={{ fontSize: T.font.size.md, fontWeight: T.font.weight.medium, color: T.color.textPrimary, fontFamily: T.font.family }}>Left without an answer</Box>
            {left && <Box component="span" sx={{ fontSize: T.font.size.sm, color: T.color.textSecondary, marginLeft: '8px', fontFamily: T.font.family }}>{left.total} {left.total === 1 ? 'item' : 'items'}</Box>}
          </Box>
          {/* 'Sorted by' names the column (N5 pt 15.1). Removed 2 Nov 2026 (N5 pt 15.1): 'Sorted by Left without an answer'. */}
          <Typography sx={{ fontSize: T.font.size.sm, color: T.color.textSecondary, fontFamily: T.font.family }}>Sorted by {{ exit_point: 'Exit point', external: 'External', internal: 'Internal' }[leftSort.by]}</Typography>
        </Box>
        <Box sx={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              {/* External and Internal each sort, with no 'Left without an answer' heading above them (N5 pt 15.1).
                  Removed 2 Nov 2026 (N5 pt 15.1): that heading over both columns, with its sort, and the second
                  header row it needed. */}
              <tr>
                <Th>Content</Th>
                <Th>Type</Th>
                {showExt && <SortTh right active={leftSort.by === 'external'} order={leftSort.order} onClick={() => sortLeft('external')}>External</SortTh>}
                {showInt && <SortTh right active={leftSort.by === 'internal'} order={leftSort.order} onClick={() => sortLeft('internal')}>Internal</SortTh>}
                <SortTh right active={leftSort.by === 'exit_point'} order={leftSort.order} onClick={() => sortLeft('exit_point')}>Exit point</SortTh>
                <Th right>Average rating</Th>
                <Th right>Last edited</Th>
                <Th right>Action</Th>
              </tr>
            </thead>
            <tbody>
              {(left?.items || []).map((row) => (
                <React.Fragment key={row.id}>
                <tr>
                  <Cell>
                    {row.content_name}
                    {/* How many questions ended in a No on this item and the latest of them, on one line (N5 pt 15); clicking
                        it shows the latest 2 under the row, clicking again hides them (agreed 14 and 23 Oct). An AI summary
                        of them is parked (PK16). */}
                    {row.question_count > 0 && (
                      <Typography
                        role="button"
                        tabIndex={0}
                        aria-expanded={openQuestions.has(row.id)}
                        onClick={() => toggleQuestions(row.id)}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleQuestions(row.id); } }}
                        title={row.latest_question || ''}
                        sx={{ fontSize: T.font.size.xs, color: T.color.textSecondary, fontFamily: T.font.family, marginTop: '3px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '420px', cursor: 'pointer', '&:hover': { color: T.color.textPrimary } }}
                      >
                        {questionLine(row)}
                      </Typography>
                    )}
                  </Cell>
                  <Cell><TypeTag type={row.content_type} /></Cell>
                  {/* People left without an answer, each once (K6.1 pt 14). */}
                  {showExt && <Cell right>{row.external_people}</Cell>}
                  {showInt && <Cell right>{row.internal_people}</Cell>}
                  <Cell right muted><ExitPoint exitPoint={row.avg_exit_time} /></Cell>
                  <Cell right>{row.avg_rating == null ? '-' : Number(row.avg_rating).toFixed(1)}</Cell>
                  {/* Last edited (N5 pt 15): whether the item changed since people were left without an answer. */}
                  <Cell right muted>{lastEditedText(row.last_edited)}</Cell>
                  <Cell right>
                    {/* The theme's one outlined button (K2.3 pt 15). Removed 2 Nov 2026 (K2.3 pt 15): its own light
                        grey border and grey text on a clickable box. */}
                    <Button variant="outlined" size="small" onClick={e => handleEditContent(e, row.id)}>
                      Edit
                    </Button>
                  </Cell>
                </tr>
                {openQuestions.has(row.id) && (row.latest_questions || []).length > 0 && (
                  <tr>
                    <td colSpan={6 + (showExt ? 1 : 0) + (showInt ? 1 : 0)} style={{ padding: '0 18px 12px', borderBottom: `0.5px solid ${T.color.borderTertiary}` }}>
                      {/* The latest 2 questions, each with the people who asked it and then said No on this item, and
                          'Asked more than once by N people' when anyone did, as on Search Intelligence (N5 pts 10, 15). */}
                      {row.latest_questions.map((q, i) => (
                        <Box key={i} sx={{ padding: '6px 0 0 12px', borderLeft: `2px solid ${T.color.borderTertiary}` }}>
                          <Typography sx={{ fontSize: T.font.size.sm, color: T.color.textPrimary, fontFamily: T.font.family }}>
                            {q.question} · {q.people} {q.people === 1 ? 'person' : 'people'}
                          </Typography>
                          {q.asked_more_than_once > 0 && (
                            <Typography sx={{ fontSize: T.font.size.xs, color: T.color.textSecondary, fontFamily: T.font.family }}>
                              Asked more than once by {q.asked_more_than_once} {q.asked_more_than_once === 1 ? 'person' : 'people'}
                            </Typography>
                          )}
                        </Box>
                      ))}
                    </td>
                  </tr>
                )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </Box>
        {left && left.total > PER_PAGE && (
          <InsightsPager page={leftPage} total={left.total} perPage={PER_PAGE} onChange={setLeftPage} />
        )}
      </Box>
      )}

      {/* Removed 14 Oct 2026 (N5 pt 18): the 'Low-engagement content' section (items with fewer than 10 views and
          under 5 completions, as archive candidates) with its Archive action and dialog. No Low engagement section in
          v1; a filter on the content list for content nobody has opened is parked (PK20). */}
    </Box>
  );
};


export default ContentHealth;
